import React, {
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { MoveFocusInside } from "react-focus-lock";

import { ContentStep } from "./ContentStep";
import { ContentStepperContextProvider } from "./context/ContentStepperContext";
import { StyledContentWizardContainer, StyledStepWrapper } from "./Styles";
import { SlideLeftRightTransition, ResizeTransition } from "../Transitions";

export interface IContentStepper {
  children: ReactNode;
  className?: string;
  step?: number;
  onStepChange?: (step: number) => void;
}

export const ContentStepper = forwardRef<HTMLDivElement, IContentStepper>(
  ({ children, className, step: controlledStep, onStepChange }, ref) => {
    const childrenCollection = useMemo(
      () => React.Children.toArray(children),
      [children]
    );

    const [internalStep, setInternalStep] = useState(0);
    const [enteredStep, setEnteredStep] = useState(0);
    const [height, setHeight] = useState("auto");
    const previousStepRef = useRef(0);

    // Use controlled step if provided, otherwise use internal state
    const activeStep =
      controlledStep !== undefined ? controlledStep : internalStep;

    // Calculate direction synchronously based on step change
    const isLeft = useMemo(() => {
      const prevStep = previousStepRef.current;
      const direction = activeStep > prevStep;
      previousStepRef.current = activeStep;
      return direction;
    }, [activeStep]);

    const setActiveStep = useCallback(
      (newStep: number) => {
        if (newStep === activeStep) return;

        if (controlledStep === undefined) {
          setInternalStep(newStep);
        }

        onStepChange?.(newStep);
      },
      [activeStep, controlledStep, onStepChange]
    );

    const goNext = useCallback(() => {
      const nextIndex = Math.min(activeStep + 1, childrenCollection.length - 1);
      setActiveStep(nextIndex);
    }, [activeStep, childrenCollection.length, setActiveStep]);

    const goPrevious = useCallback(() => {
      const prevIndex = Math.max(activeStep - 1, 0);
      setActiveStep(prevIndex);
    }, [activeStep, setActiveStep]);

    const handleHeight = useCallback((element: HTMLElement) => {
      setHeight(`${element.offsetHeight}px`);
    }, []);

    const handleEntered = useCallback((stepIndex: number) => {
      setEnteredStep(stepIndex);
    }, []);

    const renderSteps = useMemo(() => {
      return childrenCollection.map((child, index) => {
        const element = child as ReactElement;

        if (element?.type === ContentStep) {
          const stepContent = React.cloneElement(element, {
            key: `step-${index}`,
            index,
            isFirst: index === 0,
            isLast: index === childrenCollection.length - 1,
          });

          return (
            <SlideLeftRightTransition
              key={`transition-${index}`}
              in={activeStep === index}
              direction={isLeft ? "left" : "right"}
              onEnter={handleHeight}
              onEntered={() => handleEntered(index)}
            >
              <MoveFocusInside disabled={enteredStep !== index}>
                <StyledStepWrapper>{stepContent}</StyledStepWrapper>
              </MoveFocusInside>
            </SlideLeftRightTransition>
          );
        }

        return null;
      });
    }, [
      activeStep,
      childrenCollection,
      isLeft,
      enteredStep,
      handleHeight,
      handleEntered,
    ]);

    return (
      <ContentStepperContextProvider
        value={{
          activeStep,
          setActiveStep,
          goNext,
          goPrevious,
          direction: isLeft ? "left" : "right",
          totalSteps: childrenCollection.length,
        }}
      >
        <ResizeTransition in style={{ height, width: "100%" }}>
          <StyledContentWizardContainer
            ref={ref}
            className={className}
            $height={height}
          >
            {renderSteps}
          </StyledContentWizardContainer>
        </ResizeTransition>
      </ContentStepperContextProvider>
    );
  }
);

ContentStepper.displayName = "ContentStepper";
